import { useEffect } from "react";
import { useParams } from "react-router";
import { useSetAtom, useAtomValue } from "jotai";
import { repoApi, orgApi, type Repository } from "../../api/client";
import { authAtom } from "../../stores/auth";
import {
  accountsAtom,
  allReposAtom,
  galaxyLoadingAtom,
  selectedAccountAtom,
  setSelectedAccountAtom,
} from "../../stores/galaxy";
import { setSelectedDomainAtom } from "../../stores/observatory";

// useGalaxyInit enumerates the galaxies the user can see (their own
// login + connected GitHub orgs) and prefetches the active repo list
// for the URL galaxy. The repo list is the only piece that needs a
// galaxy scope here — the galaxy enumeration itself comes from
// orgApi.memberships() which is user-scoped (JWT user_id), not
// galaxy-scoped, so the user can switch galaxies before any galaxy
// has been "selected".
//
// The reposAtom is computed downstream from allReposAtom + the
// SELECTED galaxy — but pages that need exact, current-galaxy repos
// should derive `repos.filter(r => r.full_name.startsWith(account + "/"))`
// from useParams().account directly, not lean on the atom.
export function useGalaxyInit() {
  const { account: urlAccount } = useParams<{ account: string }>();
  const auth = useAtomValue(authAtom);
  const selectedAccount = useAtomValue(selectedAccountAtom);
  const setOrgs = useSetAtom(accountsAtom);
  const setAllRepos = useSetAtom(allReposAtom);
  const setLoading = useSetAtom(galaxyLoadingAtom);
  const setSelectedOrg = useSetAtom(setSelectedAccountAtom);
  const setSelectedDomain = useSetAtom(setSelectedDomainAtom);

  useEffect(() => {
    setLoading(true);
    const userLogin = auth.user?.login || "";
    // Boot galaxy: prefer URL, fall back to JWT user (their personal
    // galaxy) when the user is at "/" before redirect resolves.
    const bootGalaxy = urlAccount || userLogin;

    Promise.all([
      bootGalaxy
        ? repoApi.list(bootGalaxy).catch(() => [] as Repository[])
        : Promise.resolve([] as Repository[]),
      orgApi.memberships().catch(() => []),
    ])
      .then(([repoData, memberships]) => {
        const active = (repoData || []).filter((r) => r.is_active);
        setAllRepos(active);

        // accountsAtom is the MEMBER-galaxy list (own personal galaxy + connected
        // orgs). It must NOT be inferred from the visible repo list: on a SHARED
        // galaxy a read-only guest CAN list that galaxy's repos, so adding their
        // owners here leaked the shared galaxy into accountsAtom — which made it
        // read as a "member" everywhere (useIsSharedGuest short-circuited guest→
        // false, so the observe button showed and only 403'd; the GalaxySwitcher
        // double-listed it under both Member and Shared). Membership comes ONLY
        // from the user-scoped sources below.
        const orgSet = new Set<string>();
        if (userLogin) orgSet.add(userLogin);

        // Connected orgs from memberships (user-scoped endpoint — these are the
        // galaxies the user actually has member/admin access to).
        for (const m of memberships || []) {
          if (m.connected) orgSet.add(m.login);
        }

        const orgList = Array.from(orgSet).sort();
        setOrgs(orgList);

        // Seed the atom mirror ONLY for the bare entry point ("/") — there is no
        // URL galaxy yet, so pick a default. On a real galaxy URL, Layout owns the
        // write-through from useParams (incl. SHARED galaxies, which are
        // intentionally absent from orgList); seeding there would bounce a shared-
        // galaxy visit back to a member galaxy.
        if (!urlAccount && orgList.length > 0 && (!selectedAccount || !orgList.includes(selectedAccount))) {
          setSelectedOrg(orgList[0]);
          setSelectedDomain("");
        }
      })
      .finally(() => setLoading(false));
  }, [auth.user, urlAccount]);
}
